import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ColDef, DoesFilterPassParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { CustomSetFilter } from '~/components';
import { createDateCol } from '~/components/agGrid/columns/dateColumns';
import { DeaLicenseFormValues } from './types';

export const deaColumns = (): ColDef<DeaLicenseFormValues>[] => {
  return [
    {
      field: 'validFlag',
      maxWidth: 40,
      filter: false,
      sortable: false,
      headerName: '',
      cellRenderer: ({ data }: CustomCellRendererProps) => {
        if (!data || data.validFlag !== false) return null;
        const warnings = data.warnings as Record<string, string> | undefined;
        return (
          <Popover className="relative flex justify-center h-full items-center">
            <PopoverButton
              className="focus:outline-none cursor-pointer"
              aria-label="View validation issues"
            >
              <ExclamationTriangleIcon className="h-5 w-5 text-warning mr-2" />
            </PopoverButton>
            <PopoverPanel
              anchor="right start"
              transition
              className="z-50 bg-white p-3 rounded-lg shadow-lg transition duration-200 ease-in-out [--anchor-gap:8px] data-closed:-translate-y-1 data-closed:opacity-0 min-w-48"
            >
              <p className="body-text-sb text-content-primary mb-1">Validation Issues</p>
              {warnings && Object.keys(warnings).length > 0 ? (
                <ul className="list-disc pl-4 text-sm text-grey-500">
                  {Object.entries(warnings).map(([field, message]) => (
                    <li key={field}>{message}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-grey-500">
                  This license has validation issues. Click Edit to review.
                </p>
              )}
            </PopoverPanel>
          </Popover>
        );
      },
    },
    {
      colId: 'deaNum',
      field: 'deaNum',
      headerName: 'Dea Number',
    },
    { colId: 'stateCode', field: 'stateCode', headerName: 'Issuing State' },
    { colId: 'schedule', field: 'schedule', headerName: 'Schedule' },
    createDateCol('issueDate', 'Issue Date'),
    createDateCol('expiryDate', 'Expiration Date'),
    {
      field: 'restrictedUseFlag',
      headerName: 'Restricted Use',
      filter: {
        component: CustomSetFilter,
        doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
          if (!model || model.length === 0) return true;
          const value = node.data?.restrictedUseFlag ? 'Restricted' : 'Unrestricted';
          return model.includes(value);
        },
      },
      valueGetter: ({ data }) => (data?.restrictedUseFlag ? 'Restricted' : 'Unrestricted'),
      comparator: (valueA: string, valueB: string) => valueA.localeCompare(valueB),
    },
    {
      colId: 'status',
      field: 'activeFlag',
      headerName: 'Status',
      filter: {
        component: CustomSetFilter,
        doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
          if (!model || model.length === 0) return true;
          const value = node.data?.activeFlag ? 'Active' : 'Inactive';
          return model.includes(value);
        },
      },
      valueGetter: ({ data }) => (data?.activeFlag ? 'Active' : 'Inactive'),
      comparator: (valueA: string, valueB: string) => valueA.localeCompare(valueB),
    },
  ];
};
